import { useEffect, useState } from "react";
import { ClassListType } from "utils/interfaces";
import ClassesCardsList from "../common/ClassesCardsList";
import CreateEditClass from "../create-edit-class-pop-up";
import SavedSuccessfulPanel from "../saved-successful-pop-up";
import { motion } from "framer-motion";
import { usePlatform } from "components/utils/PlatformContext";
import { singlify } from "utils/utils";
import {
  defaultBgVariants,
  defaultPanelVariants,
} from "../common/defaultValues";
import PopUpHeader from "../common/PopUpHeader";

interface AssignClassPanelProps {
  selectedClasses: ClassListType[];
  setShowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedClasses: React.Dispatch<React.SetStateAction<ClassListType[]>>;
}

/**
 * Element that represent the pop up panel for adding/creating a class
 * @param selectedClasses List of selected classes (based on external decisions. i.e: selected to be assigned
 *                        for lessons, for organisations, for students, etc.).
 * @param setShowPopUp Function used to define if the pop should be showed or closed.
 * @param setSelectedClasses Function used to set the selected classes.
 * @returns
 */
const AssignClassPanel: React.FC<AssignClassPanelProps> = ({
  selectedClasses,
  setShowPopUp,
  setSelectedClasses,
}) => {
  // Get the context parameters
  const { classesTerminology } = usePlatform();

  // States definition
  const [popUpState, setPopUpState] = useState<"main" | "create-new">("main");
  const [showSaveSuccessPopUp, setShowSaveSuccessPopUp] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);

  // Map the selected classes into _ids
  const selectedClassesIds: string[] = selectedClasses
    .map((item: ClassListType) => {
      if (item && item._id) return item._id;
      else return null;
    })
    .filter((item: string | null): item is string => item !== null);

  /**
   * Handle clicking in the black screen
   */
  const handleClickBlackScreen = () => {
    // Prevent closing for these types
    if (popUpState === "create-new") {
      // If the pop up state is one of these ones, then don't allow to
      // close when clicking the black screen
      if (
        ["create-new", "add-teacher"].includes(popUpState) &&
        !showSaveSuccessPopUp
      ) {
        // Activate the warning message
        setShowWarningMessage(true);
        // And finish the function
        return;
      }
    }

    // Close the pop up
    setShowPopUp(false);
    // Force close the warning message
    setShowWarningMessage(false);
  };

  /**
   * Definition of the function when pressed back button
   */
  const handlePressBack = () => {
    // Check the state of create-new
    if (popUpState === "create-new") {
      // If we are using the create new option, then close the pop up
      // once clicked (we will not want to watch the assign menu when
      // just creating)
      setPopUpState("main");
    }
    // Otherwise, close the pop up
    else closePopUpAction();
  };

  const closePopUpAction = () => {
    // Ensure the warning message is closed
    if (setShowWarningMessage) setShowWarningMessage(false);

    // Close the pop up
    setShowPopUp(false);
  };

  /**
   * Function to execute once clicked "Create another"
   */
  const handleCreateAnother = () => {
    // Hide the success pop up
    setShowSaveSuccessPopUp(false);

    // And go to create-new state in the pop up
    setPopUpState("create-new");
  };

  /**
   * Definition of the Add or remove action
   * @param id of the selected id in the pop up menu
   * @param action "add" or "remove" "selectedData._id" from "id"
   */
  const selectAction = async (
    classItem: ClassListType,
    action: "add" | "remove",
  ) => {
    // Add to the current selected contact data ids
    if (action === "add") {
      // Add to selected classes
      setSelectedClasses((prev) => [...prev, classItem]);
    }
    // Or delete from it
    else if (action === "remove") {
      setSelectedClasses((prev) => prev.filter((c) => c._id !== classItem._id));
    }
  };

  /**
   * Timer for the warning pop up message
   */
  useEffect(() => {
    if (showWarningMessage) {
      // Starting the timer
      const timer = setTimeout(() => {
        // Once finished, close the pop up
        if (setShowWarningMessage) setShowWarningMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showWarningMessage]);

  /**
   * Adding a listener for keyboard shortcuts
   */
  useEffect(() => {
    // Function to use on escape
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handlePressBack();
      }
    };

    // Adding the lister
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [popUpState]);

  return (
    <>
      <motion.div
        key="assign-class-pop-up-blurred-bg"
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={handleClickBlackScreen}
      />
      {popUpState === "main" && (
        <motion.div
          key="assign-class-pop-up-panel"
          className="fixed w-[462px] h-fit left-0 right-0 ml-auto mr-auto p-6 bottom-5 bg-white rounded-3xl z-30"
          variants={defaultPanelVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.1, ease: "easeOut" }}
        >
          {!showSaveSuccessPopUp && (
            <div className="relative w-full h-fit max-h-[80vh] flex-col justify-start items-start gap-6 inline-flex">
              <PopUpHeader
                key="assign-class-pop-up-header"
                title="Class"
                handlePressBack={handlePressBack}
                createNewOptions={{
                  text: "Create new",
                  onClick: () => setPopUpState("create-new"),
                }}
              />
              <ClassesCardsList
                selectedClasses={selectedClassesIds}
                closeOnceClickOnCard={false}
                onDoneCloseClick={() => setShowPopUp(false)}
                selectAction={selectAction}
                showDoneButton={false}
              />
            </div>
          )}
          {showSaveSuccessPopUp && (
            <SavedSuccessfulPanel
              key="assign-class-saved-successful-panel"
              title={`${singlify(classesTerminology)} Created Successfully`}
              paragraphs={[
                `The new ${singlify(
                  classesTerminology,
                )} has been successfully created.`,
              ]}
              createText="Create another"
              onCreateAnother={handleCreateAnother}
              onClose={() => setShowSaveSuccessPopUp(false)}
            />
          )}
        </motion.div>
      )}
      {popUpState === "create-new" && (
        <CreateEditClass
          key="assign-class-create-edit-class"
          handleClosePopUp={() => setPopUpState("main")}
        />
      )}
    </>
  );
};

export default AssignClassPanel;
